import React, {useState} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {getBenchlingAppConfigurationQuery} from './__generated/getBenchlingAppConfigurationQuery';
import BenchlingAppConfigurationArrayElement from './BenchlingAppConfigurationArrayElement';
import {UpdateArrayElementValidationCallback} from './validation/ValidationTypes';

type ConfigMember = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['configuration'][number];

type OutputSubdependency = NonNullable<ConfigMember['output']>;

type OptionSubdependency = NonNullable<ConfigMember['options']>[number];

type FieldSubdependency = NonNullable<ConfigMember['fieldDefinitions']>[number];

type ArrayElement = NonNullable<ConfigMember['elements']>[number];

type ArrayElementDefinition = NonNullable<ConfigMember['elementDefinition']>[number];

function makeEmptySubdependencies(
  subdependencies: Array<FieldSubdependency | OptionSubdependency> | null
): Array<FieldSubdependency | OptionSubdependency> | null {
  return subdependencies != null
    ? subdependencies.map((subdep) => ({
        ...subdep,
        value: null,
      }))
    : null;
}

function makeEmptyOutput(output: OutputSubdependency | null): OutputSubdependency | null {
  return output != null
    ? {
        ...output,
        fieldDefinitions: makeEmptySubdependencies(output.fieldDefinitions) || [],
      }
    : null;
}

function makeNewBlankElement(
  elementDefinition: Array<ArrayElementDefinition>,
  labelNumberStr: string
): ArrayElement {
  return {
    __typename: 'BenchlingAppConfigArrayElementNode',
    name: `Element ${labelNumberStr}`,
    elementConfigs: elementDefinition.map((e) => ({
      ...e,
      value: null,
      options: makeEmptySubdependencies(e.options),
      fieldDefinitions: makeEmptySubdependencies(e.fieldDefinitions),
      output: makeEmptyOutput(e.output),
    })),
  };
}

export interface Props {
  arrayName: string;
  elementDefinition: Array<ArrayElementDefinition>;
  elements: Array<ArrayElement>;
  minElements: number | null;
  maxElements: number | null;
  onChangeElements: (newElements: Array<ArrayElement>) => void;
  updateArrayElementValidation: UpdateArrayElementValidationCallback;
}

export default function BenchlingAppConfigurationArrayRow({
  arrayName,
  elementDefinition,
  elements,
  minElements,
  maxElements,
  onChangeElements,
  updateArrayElementValidation,
}: Props): JSX.Element {
  const [nextLabelNumber, updateNextLabelNumber] = useState(elements.length);
  const [createdElementIdxs, updateCreatedElementIdxs] = useState(new Set());

  function changeElement(changedElement: ArrayElement, idx: number): void {
    const newElements = [...elements.slice(0, idx), changedElement, ...elements.slice(idx + 1)];
    onChangeElements(newElements);
  }

  function removeElement(idx: number): void {
    const newElements = [...elements.slice(0, idx), ...elements.slice(idx + 1)];
    onChangeElements(newElements);

    const newIdxs = new Set(createdElementIdxs);
    newIdxs.delete(idx);
    updateCreatedElementIdxs(newIdxs);
  }

  function appendNewElement(): void {
    const newLabelNumber = nextLabelNumber + 1;
    updateNextLabelNumber(newLabelNumber);
    const newLabelNumberStr = newLabelNumber.toString();

    const newElements = [...elements, makeNewBlankElement(elementDefinition, newLabelNumberStr)];
    onChangeElements(newElements);

    const newIdxs = new Set(createdElementIdxs);
    newIdxs.add(elements.length);
    updateCreatedElementIdxs(newIdxs);
  }

  function cannotAddElements(): boolean {
    return Boolean(maxElements) && elements.length >= (maxElements ?? 0);
  }

  return (
    <div>
      <div>
        {elements &&
          elements.map((element: ArrayElement, i: number) => (
            <BenchlingAppConfigurationArrayElement
              arrayName={arrayName}
              element={element}
              key={element.name}
              forceExpand={createdElementIdxs.has(i)}
              changeElement={(val) => {
                changeElement(val, i);
              }}
              removeElement={() => {
                removeElement(i);
                updateArrayElementValidation(arrayName + element.name, null);
              }}
              updateArrayElementValidation={updateArrayElementValidation}
            />
          ))}
      </div>
      <TooltipWrapper
        tooltipTitle={
          cannotAddElements() ? `Cannot add any more elements: maximum of ${maxElements} reached` : ''
        }
      >
        <Button
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.MINI}
          icon="compose"
          text="Add element"
          disabled={cannotAddElements()}
          onClick={appendNewElement}
        />
      </TooltipWrapper>
    </div>
  );
}
